import { Home, Eye, CreditCard, LogOut, LogIn, ShieldAlert, Megaphone, LayoutDashboard, Trophy, Shield, PanelLeftClose, PanelLeft, FlaskConical, Lightbulb, Target, TrendingUp } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";

const AppSidebar = () => {
  const { user, signOut } = useAuth();
  const { hasRole, isPremium } = useUserRole();
  const showBilling = hasRole("premium");
  const isAdmin = hasRole("admin");
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const navigate = useNavigate();
  const location = useLocation();


  const handleManageBilling = async () => {
    const { data, error } = await supabase.functions.invoke("customer-portal");
    if (error || !data?.url) {
      toast.error("Could not open billing portal");
      return;
    }
    window.location.href = data.url;
  };

  const mainItems = user
    ? [{ title: "My Dashboard", url: "/", icon: LayoutDashboard }]
    : [];

  const researchItems = [
    { title: "Global Leaderboard", url: "/leaderboard", icon: Trophy },
    { title: "Asset Research", url: "/research", icon: FlaskConical },
  ];

  const investItems = isPremium ? [
    { title: "Thesis Builder", url: "/thesis", icon: Lightbulb },
    { title: "Asset Assessment", url: "/assessment", icon: Target },
  ] : [];

  const valuationItems = isPremium ? [
    { title: "Valuation", url: "/valuation/lifecycle", icon: TrendingUp },
  ] : [];

  const adminItems = [
    { title: "Home (Legacy)", url: "/legacy-main", icon: Home },
    { title: "Landing", url: "/landing", icon: Megaphone },
    { title: "Admin Dashboard", url: "/admin", icon: Shield },
  ];

  return (
    <Sidebar collapsible="icon" className="border-r border-border bg-background">
      <SidebarHeader className="border-b border-border p-3">
        <div className="flex items-center justify-between">
          {!collapsed ? (
            <button
              onClick={() => navigate("/")}
              className="font-mono font-bold text-sm text-primary tracking-wider hover:opacity-80 transition-opacity flex items-center gap-2"
            >
              <ShieldAlert className="w-4 h-4 shrink-0" />
              FINFLUENCER AUDIT
            </button>
          ) : (
            <button
              onClick={() => navigate("/")}
              className="hover:opacity-80 transition-opacity"
            >
              <ShieldAlert className="w-4 h-4 text-primary" />
            </button>
          )}
          <button
            onClick={() => toggleSidebar()}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {mainItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {mainItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                        activeClassName="bg-muted text-primary font-medium"
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-widest">Research</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {researchItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end
                      className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                      activeClassName="bg-muted text-primary font-medium"
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {investItems.length > 0 && (
          <SidebarGroup>
            {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-widest">Invest</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {investItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                        activeClassName="bg-muted text-primary font-medium"
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {valuationItems.length > 0 && (
          <SidebarGroup>
            {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-widest">Valuations</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {valuationItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                        activeClassName="bg-muted text-primary font-medium"
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isAdmin && (
          <SidebarGroup>
            {!collapsed && <SidebarGroupLabel className="text-xs text-muted-foreground uppercase tracking-widest">Admin</SidebarGroupLabel>}
            <SidebarGroupContent>
              <SidebarMenu>
                {adminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <NavLink
                        to={item.url}
                        end
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                        activeClassName="bg-muted text-primary font-medium"
                      >
                        <item.icon className="w-4 h-4 shrink-0" />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-2">
        <SidebarMenu>
          {user ? (
            <>
              {showBilling && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={handleManageBilling}
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer"
                  >
                    <CreditCard className="w-4 h-4 shrink-0" />
                    {!collapsed && <span>Manage billing</span>}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => signOut()}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors cursor-pointer"
                >
                  <LogOut className="w-4 h-4 shrink-0" />
                  {!collapsed && <span>Sign out</span>}
                </SidebarMenuButton>
              </SidebarMenuItem>
              {!collapsed && (
                <div className="flex items-center gap-2 px-3 py-2 mt-1">
                  {user.user_metadata?.avatar_url ? (
                    <img src={user.user_metadata.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                  ) : null}
                  <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                    {user.user_metadata?.full_name || user.email?.split("@")[0]}
                  </span>
                </div>
              )}
            </>
          ) : (
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => navigate("/login")}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-primary hover:bg-muted/50 hover:text-primary/80 transition-colors cursor-pointer"
              >
                <LogIn className="w-4 h-4 shrink-0" />
                {!collapsed && <span>Sign in</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
